import { Button, FieldSet, Heading, Legend, Paragraph, Toast, useToast } from '@zillow/constellation-10';
import { Label, LabeledControl, Modal, Radio } from '@zillow/constellation-10';
import { useMemo, useState } from 'react';
import { TourActionType } from '@zg-rentals/ts-z-calendar-client';
import { usePerformTourAction } from '@zg-rentals/touring';
import usePageIdentifier from '../../../hooks/usePageIdentifier';
import useUpdateStatusLabelCache from '../../../hooks/useUpdateStatusLabelCache';
import {
  emitInboxTourModalActionEvent,
  emitLeadMgmtTourModalActionEvent,
  INBOX_TOUR_MODAL_ACTION_EVENT_TYPE_ID,
  LEAD_MGMT_TOUR_MODAL_ACTION_EVENT_TYPE_ID,
} from '../../../constants/clickstreamEvents/tourScheduling';
import { PAGE_NAME } from '../../../constants/pageName';
import getRenterFirstName from '../../../utils/getRenterFirstName';
import { useAuth } from '../../../AuthProvider';

type Props = {
  action: TourActionType.IRENE_CANCEL | TourActionType.IRENE_DECLINE;
  conversationId: string;
  leadId?: string | null;
  listingAlias: string;
  onClose: () => void;
  // Drilled from the parent surface (inbox: getMessages cache; lead management: lead row) —
  // this modal renders where no getMessages poller is guaranteed to exist.
  renterName: string;
  shouldShowCancellationReasons: boolean;
  tourDateTime: string;
  tourId: string;
};

const TOUR_CANCELLATION_REASONS = [
  'Already found a renter',
  'No longer plan to rent',
  'Need to reschedule the tour time',
  'Rather not answer',
] as const;

const TOUR_ACTIONS = {
  [TourActionType.IRENE_CANCEL]: {
    title: 'Cancel this tour?',
    legend: 'Reason for canceling',
    status: 'canceled',
    cta: 'Cancel tour',
  },
  [TourActionType.IRENE_DECLINE]: {
    title: 'Decline this tour?',
    legend: 'Reason for declining',
    status: 'declined',
    cta: 'Decline tour',
  },
} as const;

export default function TourCancellationModal({
  action,
  conversationId,
  leadId,
  listingAlias,
  onClose,
  renterName,
  shouldShowCancellationReasons,
  tourDateTime,
  tourId,
}: Props) {
  const { enqueueToast } = useToast();
  const { user } = useAuth();
  const currentPage = usePageIdentifier();
  const updateStatusLabelCache = useUpdateStatusLabelCache({
    conversationId,
    listingAlias,
  });
  const [cancellationReason, setCancellationReason] = useState('');

  const { mutate: performTourAction, isPending } = usePerformTourAction({
    errorDetails: 'TourCancellationModal - Error cancelling or declining tour',
  });

  const tourAction = TOUR_ACTIONS[action];
  const renterFirstName = useMemo(() => getRenterFirstName(renterName || ''), [renterName]);
  const content = `This will let ${renterFirstName} know that the tour scheduled for ${tourDateTime} is ${tourAction.status}. If you need to follow up with them or ask them to reschedule, you can send them a message.`;
  const disableActionButton = (shouldShowCancellationReasons && !cancellationReason) || isPending;

  const handleUpdateTourStatus = () => {
    if (currentPage === PAGE_NAME.leadManagement) {
      emitLeadMgmtTourModalActionEvent(user, { listingAlias }, { leadId });
    } else if (currentPage === PAGE_NAME.inbox) {
      emitInboxTourModalActionEvent(user);
    }

    performTourAction(
      {
        postData: {
          actionType: action,
          listingAlias,
          tourId,
        },
      },
      {
        onSuccess: () => {
          onClose();
          const actionText = action === TourActionType.IRENE_DECLINE ? 'request' : '';
          const toastMessage = `Tour ${actionText} for ${tourDateTime} was ${tourAction.status}.`;
          enqueueToast(<Toast tone="success">{toastMessage}</Toast>);
          updateStatusLabelCache(action);
        },
        onError: () => {
          onClose();
          enqueueToast(<Toast tone="critical">{`Tour couldn’t be ${tourAction.status}. Wait and try again.`}</Toast>);
        },
      },
    );
  };

  return (
    <Modal
      size="sm"
      open={true}
      onOpenChange={(open) => !open && onClose()}
      body={
        <>
          <Heading fontFamily="heading-alt" level={2} textStyle="heading-sm" css={{ marginBottom: 'default' }}>
            {tourAction.title}
          </Heading>

          <Paragraph>{content}</Paragraph>

          {shouldShowCancellationReasons && (
            <FieldSet
              legend={<Legend size="lg">{tourAction.legend}</Legend>}
              required={true}
              css={{ marginTop: 'default' }}
            >
              {TOUR_CANCELLATION_REASONS.map((reason) => (
                <LabeledControl
                  key={reason}
                  controlId={reason}
                  label={<Label>{reason}</Label>}
                  control={<Radio name="cancel-reason" />}
                  onChange={() => setCancellationReason(reason)}
                />
              ))}
            </FieldSet>
          )}
        </>
      }
      footer={
        <Button
          data-eventid={
            currentPage === PAGE_NAME.leadManagement
              ? LEAD_MGMT_TOUR_MODAL_ACTION_EVENT_TYPE_ID
              : INBOX_TOUR_MODAL_ACTION_EVENT_TYPE_ID
          }
          emphasis="filled"
          tone="critical"
          disabled={disableActionButton}
          onClick={handleUpdateTourStatus}
        >
          {tourAction.cta}
        </Button>
      }
    />
  );
}
